import { isFiniteNumber } from "@/utils/numberHelpers"
import { coerceSfForIteration } from "@/utils/sfValue"

export type TargetParamBw = 125 | 250 | 500 | 406 | 812 | 1625
export type TargetChirpType = "up-only" | "down-only" | "up-down"

export interface TargetParamModeDto {
  id: string
  sf: number | number[] | null
  bw: TargetParamBw | null
  chirp_type?: TargetChirpType | null
  sf_iteration?: boolean
  sweep_iteration?: boolean
  sweep_time?: number
  is_active: boolean
  order: number
  created_at: string
  updated_at: string | null
}

export interface TargetParamDto {
  id: string
  freq_from: number
  freq_to: number
  bw: number | null
  sf: number | number[] | null
  chirp_type?: TargetChirpType | null
  sf_iteration?: boolean
  sweep_iteration?: boolean
  sweep_time?: number
  is_video?: boolean
  polarization_angle?: 0 | 45 | 90 | null
  custom_sweeps?: boolean
  sweeps?: Array<[number, number] | number[]>
  simultaneous?: boolean
  modes?: TargetParamModeDto[]
  created_at: string
  updated_at: string | null
}

export type TargetFreqInputModeDto = "DEFAULT" | "CENTRAL"

export interface TargetLiveDto {
  id: string
  name: string | null
  is_template: boolean
  is_fake: boolean
  auto_start?: boolean
  last_lng: number | null
  last_lat: number | null
  height_above_ground?: number | null
  created_at: string
  updated_at: string | null
  creator_username: string
  status: string
  status_changed_at?: string | null
  target_lifetime_seconds?: number | null
  params?: TargetParamDto[]
  edited_at?: string | null
  edited_by?: string | null
  sync_stations?: boolean
  rfmd?: number[]
  freq_input_mode?: TargetFreqInputModeDto
  ex?: number | null
  expires_at?: number | null
  ttl_remaining_seconds?: number | null
}

export interface TargetDetailDto extends TargetLiveDto {
  params: TargetParamDto[]
}

/** Paginated list response from GET /targets/ (no stations_online). */
export interface TargetListPageDto {
  items: TargetLiveDto[]
  total: number
  page: number
  size: number
  pages: number
}

export interface TargetParamMode {
  id?: string
  sf: number[] | null
  bw: TargetParamBw | null
  chirp_type?: TargetChirpType | null
  sf_iteration?: boolean
  sweep_iteration?: boolean
  sweep_time?: number
  is_active: boolean
  order: number
  created_at?: string
  updated_at?: string | null
}

export type TargetSweep = [number, number]

export interface TargetParam {
  id?: string
  freq_from: number
  freq_to: number
  bw: number | null
  sf: number[] | null
  chirp_type?: TargetChirpType | null
  sf_iteration?: boolean
  sweep_iteration?: boolean
  sweep_time?: number
  is_video: boolean
  polarization_angle: 0 | 45 | 90 | null
  custom_sweeps?: boolean
  sweeps?: TargetSweep[]
  simultaneous?: boolean
  modes?: TargetParamMode[]
  created_at?: string
  updated_at?: string | null
}

export interface Target {
  id?: string
  name?: string
  isTemplate: boolean
  isFake?: boolean
  autoStart?: boolean
  lat: number
  lng: number
  height_above_ground?: number
  status?: string
  status_changed_at?: string | null
  target_lifetime_seconds?: number | null
  created_at?: string
  updated_at?: string | null
  creator_username?: string
  edited_at?: string | null
  edited_by?: string | null
  params?: TargetParam[]
  sync_stations?: boolean
  rfmd?: number[]
  freq_input_mode?: TargetFreqInputModeDto
  ex?: number | null
  expires_at?: number | null
  ttl_remaining_seconds?: number | null
  // Legacy optional fields still referenced by table UI
  type?: string
  height?: number
}

export interface TargetListPage {
  items: Target[]
  total: number
  page: number
  size: number
  pages: number
}

export interface TargetCreateRequest {
  name?: string
  is_template: boolean
  is_fake: boolean
  auto_start?: boolean
  last_lng: number
  last_lat: number
  height_above_sea?: number
  height_above_ground?: number
  add_target_history?: boolean
  sync_stations?: boolean
  rfmd?: number[]
  freq_input_mode?: TargetFreqInputModeDto
  params: TargetCreateParamRequest[]
}

export interface TargetPatchRequest {
  name?: string
  is_template?: boolean
  is_fake?: boolean
  auto_start?: boolean
  last_lng?: number
  last_lat?: number
  height_above_ground?: number
  status?: string
  sync_stations?: boolean
  rfmd?: number[]
  freq_input_mode?: TargetFreqInputModeDto
}

export interface TargetParamModeCreateRequest {
  sf: number[] | null
  bw: TargetParamBw | null
  chirp_type?: TargetChirpType | null
  sf_iteration?: boolean
  sweep_iteration?: boolean
  sweep_time?: number
  is_active: boolean
  order: number
}

export interface TargetParamModeUpdateRequest {
  id?: string
  sf?: number[] | null
  bw?: TargetParamBw | null
  chirp_type?: TargetChirpType | null
  sf_iteration?: boolean
  sweep_iteration?: boolean
  sweep_time?: number
  is_active?: boolean
  order?: number
}

export interface TargetCoordinatesUpdate {
  id: string
  lat: number
  lng: number
  edited_at?: string | null
  edited_by?: string | null
}

export interface TargetCreateParamRequest {
  freq_from: number
  freq_to: number
  bw: number | null
  sf: number[] | null
  chirp_type?: TargetChirpType | null
  sf_iteration?: boolean
  sweep_iteration?: boolean
  sweep_time?: number
  is_video: boolean
  polarization_angle: 0 | 45 | 90 | null
  custom_sweeps?: boolean
  sweeps?: TargetSweep[]
  simultaneous?: boolean
  modes?: TargetParamModeCreateRequest[]
}

const normalizeTargetSweeps = (
  sweeps: TargetParamDto["sweeps"] | TargetSweep[] | undefined,
): TargetSweep[] => {
  if (!Array.isArray(sweeps)) return []
  return sweeps
    .map((entry) => {
      if (!Array.isArray(entry) || entry.length < 2) return null
      const start = Number(entry[0])
      const end = Number(entry[1])
      if (!Number.isFinite(start) || !Number.isFinite(end)) return null
      return [start, end] as TargetSweep
    })
    .filter((entry): entry is TargetSweep => entry !== null)
}

const toFiniteNumber = (value: unknown, fallback = 0): number => {
  const parsed =
    typeof value === "number"
      ? value
      : typeof value === "string"
        ? Number.parseFloat(value)
        : Number.NaN

  return isFiniteNumber(parsed) ? parsed : fallback
}

const toOptionalFiniteNumber = (value: unknown): number | undefined =>
  typeof value === "number" && Number.isFinite(value) ? value : undefined

const sortTargetParamDtos = (params: TargetParamDto[]): TargetParamDto[] =>
  [...params].sort((left, right) => {
    const leftTime = Date.parse(left.created_at)
    const rightTime = Date.parse(right.created_at)
    const leftCreated = Number.isFinite(leftTime) ? leftTime : 0
    const rightCreated = Number.isFinite(rightTime) ? rightTime : 0
    if (leftCreated !== rightCreated) {
      return leftCreated - rightCreated
    }
    return left.id.localeCompare(right.id)
  })

export const mapTargetParamModeDtoToModel = (
  dto: TargetParamModeDto,
): TargetParamMode => {
  const sfIteration = dto.sf_iteration ?? dto.sweep_iteration ?? false

  return {
    id: dto.id,
    sf: coerceSfForIteration(dto.sf, sfIteration),
    bw: dto.bw,
    chirp_type: dto.chirp_type ?? "up-down",
    sf_iteration: sfIteration,
    sweep_iteration: sfIteration ? (dto.sweep_iteration ?? false) : false,
    ...(typeof dto.sweep_time === "number"
      ? { sweep_time: dto.sweep_time }
      : {}),
    is_active: dto.is_active,
    order: dto.order,
    created_at: dto.created_at,
    updated_at: dto.updated_at,
  }
}

export const mapTargetParamDtoToModel = (dto: TargetParamDto): TargetParam => {
  const sfIteration = dto.sf_iteration ?? dto.sweep_iteration ?? false
  const customSweeps = Boolean(dto.custom_sweeps) && !dto.is_video
  const sweeps = customSweeps ? normalizeTargetSweeps(dto.sweeps) : []

  return {
    id: dto.id,
    freq_from: dto.freq_from,
    freq_to: dto.freq_to,
    bw: dto.bw,
    sf: coerceSfForIteration(dto.sf, sfIteration),
    chirp_type: dto.chirp_type ?? "up-down",
    sf_iteration: sfIteration,
    sweep_iteration: sfIteration ? (dto.sweep_iteration ?? false) : false,
    ...(typeof dto.sweep_time === "number"
      ? { sweep_time: dto.sweep_time }
      : {}),
    is_video: dto.is_video ?? false,
    polarization_angle: Object.prototype.hasOwnProperty.call(
      dto,
      "polarization_angle",
    )
      ? (dto.polarization_angle ?? null)
      : 45,
    custom_sweeps: customSweeps,
    sweeps,
    simultaneous: Boolean(dto.simultaneous),
    modes: (dto.modes ?? []).map(mapTargetParamModeDtoToModel),
    created_at: dto.created_at,
    updated_at: dto.updated_at,
  }
}

export const mapTargetLiveDtoToModel = (dto: TargetLiveDto): Target => {
  const ex = toOptionalFiniteNumber(dto.ex)
  const expiresAt = toOptionalFiniteNumber(dto.expires_at)
  const ttlRemainingSeconds = toOptionalFiniteNumber(dto.ttl_remaining_seconds)

  return {
    id: dto.id,
    name: dto.name ?? "",
    isTemplate: dto.is_template ?? false,
    isFake: dto.is_fake ?? false,
    autoStart: dto.auto_start ?? true,
    lat: toFiniteNumber(dto.last_lat),
    lng: toFiniteNumber(dto.last_lng),
    height_above_ground:
      typeof dto.height_above_ground === "number"
        ? dto.height_above_ground
        : undefined,
    height:
      typeof dto.height_above_ground === "number"
        ? dto.height_above_ground
        : undefined,
    status: dto.status,
    status_changed_at: dto.status_changed_at ?? null,
    target_lifetime_seconds: dto.target_lifetime_seconds ?? null,
    created_at: dto.created_at,
    updated_at: dto.updated_at,
    creator_username: dto.creator_username,
    edited_at: dto.edited_at ?? null,
    edited_by: dto.edited_by ?? null,
    sync_stations: dto.sync_stations ?? false,
    rfmd: dto.rfmd ?? [],
    freq_input_mode: dto.freq_input_mode ?? "DEFAULT",
    params: sortTargetParamDtos(dto.params ?? []).map(mapTargetParamDtoToModel),
    ...(ex !== undefined ? { ex } : {}),
    ...(expiresAt !== undefined ? { expires_at: expiresAt } : {}),
    ...(ttlRemainingSeconds !== undefined
      ? { ttl_remaining_seconds: ttlRemainingSeconds }
      : {}),
  }
}

export const mapTargetDetailDtoToModel = (dto: TargetDetailDto): Target => ({
  ...mapTargetLiveDtoToModel(dto),
})

export const mapTargetListPageDtoToModel = (
  dto: TargetListPageDto,
): TargetListPage => ({
  items: (dto.items ?? []).map(mapTargetLiveDtoToModel),
  total: dto.total ?? 0,
  page: dto.page ?? 0,
  size: dto.size ?? 0,
  pages: dto.pages ?? 0,
})

const mapParamModesToCreateRequest = (
  modes: TargetParamModeCreateRequest[] | undefined,
) =>
  modes?.map((mode) => {
    const sfIteration = mode.sf_iteration ?? mode.sweep_iteration ?? false
    const sweepIteration = sfIteration ? (mode.sweep_iteration ?? false) : false

    return {
      sf: mode.sf,
      bw: mode.bw,
      chirp_type: mode.chirp_type ?? "up-down",
      sf_iteration: sfIteration,
      sweep_iteration: sweepIteration,
      ...(sfIteration && sweepIteration && typeof mode.sweep_time === "number"
        ? { sweep_time: mode.sweep_time }
        : {}),
      is_active: mode.is_active,
      order: mode.order,
    }
  })

export const mapTargetCreateToRequest = (target: {
  name?: string
  isTemplate?: boolean
  isFake?: boolean
  autoStart?: boolean
  lat: number
  lng: number
  height_above_sea?: number
  height_above_ground?: number
  add_target_history?: boolean
  sync_stations?: boolean
  rfmd?: number[]
  freq_input_mode?: TargetFreqInputModeDto
  params: TargetCreateParamRequest[]
}): TargetCreateRequest => ({
  name: target.name,
  is_template: target.isTemplate ?? false,
  is_fake: target.isFake ?? false,
  auto_start: target.autoStart ?? true,
  last_lng: target.lng,
  last_lat: target.lat,
  height_above_sea: target.height_above_sea,
  height_above_ground: target.height_above_ground,
  add_target_history: target.add_target_history,
  sync_stations: target.sync_stations,
  rfmd: target.rfmd,
  freq_input_mode: target.freq_input_mode ?? "DEFAULT",
  params: target.params.map((param) => {
    const sfIteration = param.sf_iteration ?? param.sweep_iteration ?? false
    const sweepIteration = sfIteration
      ? (param.sweep_iteration ?? false)
      : false

    const customSweeps =
      Boolean(param.custom_sweeps) &&
      !param.is_video &&
      Boolean(param.sweeps?.length)

    return {
      freq_from: param.freq_from,
      freq_to: param.freq_to,
      bw: param.bw,
      sf: param.sf,
      ...(param.chirp_type ? { chirp_type: param.chirp_type } : {}),
      sf_iteration: sfIteration,
      sweep_iteration: sweepIteration,
      ...(sfIteration && sweepIteration && typeof param.sweep_time === "number"
        ? { sweep_time: param.sweep_time }
        : {}),
      is_video: param.is_video,
      polarization_angle: param.polarization_angle,
      custom_sweeps: customSweeps,
      sweeps: customSweeps ? normalizeTargetSweeps(param.sweeps) : [],
      simultaneous: Boolean(param.simultaneous) && !param.is_video,
      ...(param.modes?.length
        ? { modes: mapParamModesToCreateRequest(param.modes) }
        : {}),
    }
  }),
})

export const mapTargetPatchToRequest = (target: {
  name?: string
  isTemplate?: boolean
  isFake?: boolean
  autoStart?: boolean
  lat?: number
  lng?: number
  height_above_ground?: number
  status?: string
  sync_stations?: boolean
  rfmd?: number[]
  freq_input_mode?: TargetFreqInputModeDto
}): TargetPatchRequest => {
  const payload: TargetPatchRequest = {}

  if (typeof target.name !== "undefined") payload.name = target.name
  if (typeof target.isTemplate !== "undefined") {
    payload.is_template = target.isTemplate
  }
  if (typeof target.isFake !== "undefined") {
    payload.is_fake = target.isFake
  }
  if (typeof target.autoStart !== "undefined") {
    payload.auto_start = target.autoStart
  }
  if (typeof target.lng !== "undefined") payload.last_lng = target.lng
  if (typeof target.lat !== "undefined") payload.last_lat = target.lat
  if (typeof target.height_above_ground !== "undefined") {
    payload.height_above_ground = target.height_above_ground
  }
  if (typeof target.status !== "undefined") payload.status = target.status
  if (typeof target.sync_stations !== "undefined") {
    payload.sync_stations = target.sync_stations
  }
  if (typeof target.rfmd !== "undefined") {
    payload.rfmd = target.rfmd
  }
  if (typeof target.freq_input_mode !== "undefined") {
    payload.freq_input_mode = target.freq_input_mode
  }

  return payload
}

export const mapTargetSseLocation = (
  message: {
    key?: string
    identifier?: string
    data?: any
  } | null,
): TargetCoordinatesUpdate | null => {
  if (!message || !message.key) {
    return null
  }

  const key = String(message.key)
  const payload = message.data ?? {}

  const resolveCoords = (value: any) => {
    if (!value || typeof value !== "object") return null
    const lat = toFiniteNumber(value.lat, Number.NaN)
    const lng = toFiniteNumber(value.lng, Number.NaN)
    if (!isFiniteNumber(lat) || !isFiniteNumber(lng)) return null
    return {
      lat,
      lng,
      edited_at:
        typeof value.edited_at === "string" ? value.edited_at : undefined,
      edited_by:
        typeof value.edited_by === "string" || value.edited_by === null
          ? value.edited_by
          : undefined,
    }
  }

  if (key.startsWith("targets:")) {
    const id = key.slice("targets:".length)
    const coords = resolveCoords(payload)
    if (!id || !coords) return null
    return { id, ...coords }
  }

  if (key === "targets") {
    const id = message.identifier || payload.target_id
    const coords = resolveCoords(payload)
    if (!id || !coords) return null
    return { id, ...coords }
  }

  if (key === "new_target_location") {
    const id = payload.target_id || message.identifier
    const locationPayload =
      payload.location && typeof payload.location === "object"
        ? {
            ...payload.location,
            edited_at: payload.location.edited_at ?? payload.edited_at,
            edited_by: payload.location.edited_by ?? payload.edited_by,
          }
        : payload
    const coords = resolveCoords(locationPayload)
    if (!id || !coords) return null
    return { id, ...coords }
  }

  return null
}
